<template>
  <base-slide-over ref="slideOver" :title="title" size="2xl">
    <base-form ref="form" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6" v-model="isFormValid">
      <div>{{ infoTitle }}</div>
      <base-text-field label="Full Name" placeholder="Enter full name" v-model="fullName" class="tw-mt-3" required />
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4">
        <base-email-field label="Email" placeholder="Enter email" v-model="email" required />
        <base-phone-number-input label="Phone" placeholder="Enter phone" v-model="mobile" :e164.sync="phone164" required />
      </div>
      <base-date-picker label="Date of Birth" placeholder="Enter date of birth" initial-view="year" v-model="dob" required v-if="organization.collectDobSetting" />
      <div v-if="organization.collectMailingAddressSetting" class="tw-mt-2">
        <div>Mailing Address</div>
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-4 tw-mt-3">
          <base-text-field class="tw-col-span-2" label="Line 1" placeholder="Enter line 1" v-model="mailingAddress.line1" required />
          <base-text-field label="Line 2" placeholder="Enter line 2" v-model="mailingAddress.line2" />
        </div>
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-x-4">
          <base-text-field label="City" placeholder="Enter city" v-model="mailingAddress.city" required />
          <base-select label="State" placeholder="Select state" :options="STATE_OPTIONS" label-key="name" v-model="mailingAddress.state" required />
          <base-text-field label="Zip Code" placeholder="Enter zip code" v-model="mailingAddress.postalCode" required />
        </div>
      </div>
      <div v-else>
        <base-text-field label="Zip Code" placeholder="Enter zip code" v-model="postalCode" required />
      </div>
      <div>Plan</div>
      <base-advanced-select class="tw-mt-3" label="Plan" placeholder="Select plan" :options="planOptions" v-model="selectedPlans" label-key="name" multiple required />
      <base-date-picker label="Plan Start Date" placeholder="Select start date" v-model="planStartDate" required :disabled-dates="disabledDates" />
      <base-select
        label="Sign Up Location"
        placeholder="Select a location"
        :optgroups="locationOptGroups"
        v-model="selectedLocationId"
        label-key="name"
        value-key="businessLocationId"
        required
        v-if="locations.length > 1"
      />
      <div>Card</div>
      <base-credit-card-input class="tw-mt-3" ref="creditCardInput" hide-name hide-postal-code />
    </base-form>
    <inline-error class="tw-my-3" id="create-error" :title="errorTitle" :message="errorMessage" :error="error" />
    <template #fixed-bottom>
      <div>
        <div class="tw-text-sm tw-text-gray-700 tw-px-4 sm:tw-px-6">
          By clicking Create below, the {{ organization.customerLabelSingular }} is agreeing to the
          <base-button type="text" @click="$refs.termsModal.show()">Terms of Service</base-button>.
        </div>
        <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
          <base-button type="flat" @click="$refs.slideOver.hide()" class="tw-mr-3">Cancel</base-button>
          <base-button @click="createPlan" :disabled="!isFormValid" :loading="isCreatingPlan">Create</base-button>
        </div>
      </div>
    </template>
    <terms-modal ref="termsModal" :selected-plans="selectedPlans" />
  </base-slide-over>
</template>

<script>
import TermsModal from "@/views/business/member-search/components/TermsModal";
import { STATE_OPTIONS } from "@/constants/constants";
import { mapGetters } from "vuex";
import { capitalize, uniq, orderBy } from "lodash";
import InlineError from "@/components/InlineError";

export default {
  components: {
    TermsModal,
    InlineError,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      isFormValid: true,
      isCreatingPlan: false,
      fullName: "",
      email: "",
      mobile: "",
      phone164: "",
      dob: "",
      postalCode: "",
      mailingAddress: {
        line1: null,
        line2: null,
        city: null,
        state: null,
        postalCode: null,
      },
      STATE_OPTIONS,
      planStartDate: new Date(),
      isLoadingPlans: false,
      plans: [],
      selectedPlans: [],
      selectedLocationId: null,
      token: null,
      errorTitle: "",
      errorMessage: "",
      error: null,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("location", ["location", "locations"]),
    title() {
      return `Create ${capitalize(this.organization.customerLabelSingular)} Plan`;
    },
    infoTitle() {
      return `${capitalize(this.organization.customerLabelSingular)} Info`;
    },
    planGroups() {
      return this.plans ? uniq(this.plans.map(({ planGroup }) => planGroup)).sort() : [""];
    },
    planOptions() {
      const planOptions = [];

      this.planGroups.forEach((pg) => {
        planOptions.push({
          name: pg,
          isHeader: true,
        });

        planOptions.push(...orderBy(this.plans.filter((p) => p.planGroup === pg), ["name"]));
      });

      return planOptions;
    },
    locationOptGroups() {
      return STATE_OPTIONS.filter((s) => this.locations.find((l) => l.state === s.value)).map((s) => ({
        label: s.value,
        options: this.locations.filter((l) => l.state === s.value),
      }));
    },
    disabledDates() {
      var min = new Date();
      var max = new Date();

      min.setDate(min.getDate() - 1);
      max.setDate(max.getDate() + 30);

      return { to: min, from: max };
    },
  },
  created() {
    this.getPlans();
  },
  mounted() {
    if (this.locations.length === 1) this.selectedLocationId = this.locations[0].businessLocationId;
  },
  methods: {
    show() {
      this.$refs.slideOver.show();
      this.$refs.form.reset();
      this.$refs.creditCardInput.reset();
      this.planStartDate = new Date();
    },
    hide() {
      this.error = null;
      this.errorMessage = "";
      this.errorTitle = "";
      this.$refs.slideOver.hide();
    },
    async getPlans() {
      try {
        this.isLoadingPlans = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/plans/list`);
        this.plans = response.data;
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to retrieve plans",
          message: "We were not able to retrieve the your business plans. There may have been a connection error. Please reload the page to try again.",
          error,
        });
      } finally {
        this.isLoadingPlans = false;
      }
    },
    async createPlan() {
      this.isCreatingPlan = true;
      this.token = await this.$refs.creditCardInput.createToken();

      if (!this.$refs.form.validate() || !this.token) {
        this.isCreatingPlan = false;
        return;
      }

      this.$axios
        .post(`/businesses/${this.businessId}/members`, {
          fullName: this.fullName,
          email: this.email,
          phone: this.phone164,
          postalCode: this.postalCode,
          businessLocationId: this.selectedLocationId,
          businessPlanIds: this.selectedPlans.map((x) => x.businessPlanId),
          startDate: this.planStartDate,
          tokenId: this.token.id,
          mailingAddress: this.mailingAddress,
          dob: this.dob,
        })
        .then((response) => {
          const paymentStatuses = response.data.paymentStatuses;
          const hasSuccessfulOrPendingStartStatus = Object.values(paymentStatuses).some((status) => status === "succeeded" || status === "pending start");

          if (hasSuccessfulOrPendingStartStatus) {
            this.EventBus.$emit("member_update");

            this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Member Created", message: "The member plan was activated" });

            this.$router.push(`/businesses/${this.businessId}/members/${response.data.userId}/plans`);
          } else {
            this.errorTitle = `Error activating plan`;
            this.errorMessage = "Sorry, we had trouble activating this plan.";
            this.error = "Error in payment status";
          }
        })
        .catch((error) => {
          this.errorTitle = `Error creating plan`;
          this.errorMessage = "Sorry, we had trouble activating this plan.";
          this.error = error;
        })
        .finally(() => {
          this.isCreatingPlan = false;
        });
    },
  },
};
</script>
